import PropTypes from 'prop-types';
import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { styleUtils, Tooltip } from '@ublend-npm/aulaui-next';
import { evaluateMaterialAction } from '@core/materials/permissions';
import ScheduledItemHeader from '../../../common/ScheduledItemHeader';

import {
  BUTTON_SAVE,
  BUTTON_PREVIEW,
  BUTTON_EXIT_PREVIEW,
  MATERIAL_EDIT,
  MATERIAL_UNHIDE_TOOLTIP,
  MATERIAL_SCHEDULE_TOOLTIP,
  MATERIAL_PUBLISH,
} from '../../../../../constants/texts';
import {
  Container,
  Button,
  ActionsContainer,
  PageDetailsContainer,
  IconButton,
  VisibilityOffIcon,
  VisibilityOnIcon,
  Container2,
} from './ActionBar.styled';
import SectionViewActionMenu from './SectionViewActionMenu';
import { Box } from '@ublend-npm/aulaui-core';

const renderHiddenPageInfo = () => (
  <PageDetailsContainer>
    <VisibilityOffIcon />
    This page is hidden
  </PageDetailsContainer>
);

const renderScheduledPageInfo = (scheduledFor, items, saving) => (
  <ScheduledItemHeader
    scheduledFor={scheduledFor}
    items={items}
    disabled={saving}
  />
);

const renderPageInfo = ({ hidden, scheduledFor, items, saving }) => {
  if (hidden && scheduledFor) {
    return renderScheduledPageInfo(new Date(scheduledFor), items, saving);
  }
  if (hidden) {
    return renderHiddenPageInfo();
  }
  return null;
};

const ActionBar = ({
  onUnhide,
  onSave,
  onEdit,
  onPreview,
  onExitPreview,
  onDeleteSection,
  onHideSection,
  onFindPreviousVersion,
  onImportMaterialPage,
  onExportMaterialPage,
  showUnhide,
  showSave,
  showEdit,
  showPreview,
  isPreviewing,
  canSave,
  canFindPreviousVersion,
  canExportMaterialPage,
  a11yMessage,
  saving,
  openScheduleDialog,
  onKeepHidden,
  scheduledFor,
  educatorOnly,
  canEditMaterial = true,
  canDeleteMaterial = true,
  permissions = [],
  isRbacEnabled = false,
  isStudent = false,
}) => {
  const items = [
    {
      label: 'Change scheduled time',
      action: openScheduleDialog,
    },
    {
      label: 'Undo scheduling and keep hidden',
      action: onKeepHidden,
    },
    {
      label: 'Publish now',
      action: onUnhide,
    },
  ];

  // In educator area: if user has educator area permission, they can do everything
  // Skip all permission checks when in educator area
  const effectivePermissions = !isRbacEnabled ? [] : permissions;

  // If in educator area and user has permission, allow all actions
  const allowAllInEducatorArea = educatorOnly && canEditMaterial;

  const scheduleStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('SCHEDULE_PAGE', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });
  const editStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('EDIT_PAGE', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });
  const publishStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('PUBLISH_PAGE', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });

  // Students: only show options they have permission for (hide when not allowed)
  // Educators: show all options, disabled + tooltip when not allowed
  const showSchedule =
    showUnhide &&
    !scheduledFor &&
    !educatorOnly &&
    (isStudent && isRbacEnabled ? scheduleStatus.allowed : true);
  const showEditButton =
    showEdit && (isStudent && isRbacEnabled ? editStatus.allowed : true);
  const showPublish =
    showUnhide &&
    !scheduledFor &&
    (isStudent && isRbacEnabled ? publishStatus.allowed : true);

  return (
    <Container scheduledFor={scheduledFor} hidden={showUnhide}>
      {renderPageInfo({ hidden: showUnhide, scheduledFor, items })}
      <ActionsContainer>
        {showSchedule && (
          <Tooltip
            title={
              scheduleStatus.allowed
                ? MATERIAL_SCHEDULE_TOOLTIP
                : scheduleStatus.message
            }
            position="top"
          >
            <div>
              <IconButton
                onClick={openScheduleDialog}
                label="schedule"
                size="small"
                icon={ScheduleIcon}
                disabled={!scheduleStatus.allowed}
              />
            </div>
          </Tooltip>
        )}
        {showEditButton && (
          <Tooltip
            title={editStatus.allowed ? MATERIAL_EDIT : editStatus.message}
            position="top"
          >
            <div>
              <IconButton
                type="secondary"
                onClick={onEdit}
                label={MATERIAL_EDIT}
                size="small"
                icon={EditIcon}
                disabled={!editStatus.allowed}
              />
            </div>
          </Tooltip>
        )}
        {showPreview &&
          !isPreviewing &&
          (isStudent && isRbacEnabled ? editStatus.allowed : true) && (
            <Button
              type="secondary"
              size="small"
              onClick={onPreview}
              show={showPreview}
            >
              <Container2>
                <VisibilityOnIcon size="small" />
                {BUTTON_PREVIEW}
              </Container2>
            </Button>
          )}
        {isPreviewing && (
          <Button
            type="secondary"
            size="small"
            onClick={onExitPreview}
            show={isPreviewing}
          >
            <Container2>
              <VisibilityOnIcon />
              {BUTTON_EXIT_PREVIEW}
            </Container2>
          </Button>
        )}
        {showPublish && (
          <Tooltip
            title={
              publishStatus.allowed
                ? MATERIAL_UNHIDE_TOOLTIP
                : publishStatus.message
            }
            position="bottom"
          >
            <div>
              <Button
                type="secondary"
                size="small"
                id="material-publish-button"
                onClick={onUnhide}
                show={showUnhide}
                disabled={!publishStatus.allowed}
              >
                {MATERIAL_PUBLISH}
              </Button>
            </div>
          </Tooltip>
        )}

        <Button
          type="primary"
          size="small"
          data-testid="material-save-button"
          aria-label="Save material"
          id="material-save-button"
          onClick={onSave}
          show={showSave}
          loading={saving}
          disabled={saving || !canSave}
        >
          {BUTTON_SAVE}
        </Button>
        <p css={[styleUtils.visuallyHidden()]} aria-live="polite">
          {a11yMessage}
        </p>
        {((isStudent && isRbacEnabled) || !isStudent) && (
          <SectionViewActionMenu
            educatorOnly={educatorOnly}
            canFindPreviousVersion={canFindPreviousVersion}
            canExportMaterialPage={canExportMaterialPage}
            onDeleteSection={onDeleteSection}
            onHideSection={
              !showUnhide && publishStatus.allowed ? onHideSection : null
            }
            onFindPreviousVersion={onFindPreviousVersion}
            onExportMaterialPage={onExportMaterialPage}
            onImportMaterialPage={onImportMaterialPage}
            editing={showEdit}
            canEditMaterial={canEditMaterial}
            canDeleteMaterial={canDeleteMaterial}
            permissions={permissions}
            isRbacEnabled={isRbacEnabled}
            isStudent={isStudent}
          />
        )}
      </ActionsContainer>
    </Container>
  );
};

ActionBar.propTypes = {
  ...SectionViewActionMenu.propTypes,
  scheduledFor: PropTypes.string,
  canSave: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  showUnhide: PropTypes.bool.isRequired,
  showSave: PropTypes.bool.isRequired,
  showEdit: PropTypes.bool.isRequired,
  showPreview: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  a11yMessage: PropTypes.string,
  onUnhide: PropTypes.func,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  onPreview: PropTypes.func,
  onExitPreview: PropTypes.func,
  onExportMaterialPage: PropTypes.func,
  onImportMaterialPage: PropTypes.func,
  educatorOnly: PropTypes.bool,
  openScheduleDialog: PropTypes.func,
  onKeepHidden: PropTypes.func,
  canEditMaterial: PropTypes.bool,
  canDeleteMaterial: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  isStudent: PropTypes.bool,
};

ActionBar.defaultProps = {
  ...SectionViewActionMenu.defaultProps,
  onUnhide: () => null,
  onSave: () => null,
  onEdit: () => null,
  onPreview: () => null,
  onExitPreview: () => null,
  onKeepHidden: () => null,
  openScheduleDialog: () => null,
  scheduledFor: null,
  showPreview: false,
  isPreviewing: false,
  canEditMaterial: true,
  canDeleteMaterial: true,
  permissions: [],
  isStudent: false,
};

export default ActionBar;
